import { useState, useRef, useEffect, type ChangeEvent, type FormEvent } from 'react';
import {
  calculateRoi,
  formatMoney,
  formatSignedMoney,
  formatPercent,
  formatNumber,
  type Currency,
  type CalculationType,
  type RoiResult,
  CURRENCY_LABELS,
} from './roiCalculatorCore';

const REPORT_ENDPOINT = '/roi/api/create-report.php';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseNumber(value: string): number {
  const normalized = value.replace(',', '.').replace(/\s/g, '');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function parseInteger(value: string): number {
  const parsed = Number.parseInt(value.replace(/\s/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function captureUtm(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });
  return utm;
}

interface NumberFieldProps {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  min?: number;
  max?: number;
  step?: string;
  suffix?: string;
  placeholder?: string;
}

function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
  required,
  min,
  max,
  step,
  suffix,
  placeholder,
}: NumberFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-900 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-500 mb-2 leading-snug">{hint}</p>}
      <div className="relative">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={step ?? 'any'}
          placeholder={placeholder}
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base ${suffix ? 'pr-12' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

export default function RoiCalculator() {
  const [currency, setCurrency] = useState<Currency>('BYN');
  const [type, setType] = useState<CalculationType>('sales');

  const [trainingCost, setTrainingCost] = useState('');
  const [extraCosts, setExtraCosts] = useState('0');
  const [participants, setParticipants] = useState('');
  const [monthlyRevenue, setMonthlyRevenue] = useState('');
  const [revenueGrowthPct, setRevenueGrowthPct] = useState('');
  const [marginPct, setMarginPct] = useState('');
  const [payroll, setPayroll] = useState('');
  const [productivityGrowthPct, setProductivityGrowthPct] = useState('');
  const [influencePct, setInfluencePct] = useState('');
  const [periodMonths, setPeriodMonths] = useState('');

  const [result, setResult] = useState<RoiResult | null>(null);
  const [validationError, setValidationError] = useState('');

  const [reportName, setReportName] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [reportStatus, setReportStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [reportUrl, setReportUrl] = useState('');
  const [reportError, setReportError] = useState('');

  const resultRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (result) {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const changeType = (next: CalculationType) => {
    setType(next);
    setResult(null);
    setValidationError('');
    setReportStatus('idle');
    setReportUrl('');
    setReportError('');
  };

  const handleCalculate = () => {
    setValidationError('');

    const cost = parseNumber(trainingCost);
    const extra = extraCosts.trim() === '' ? 0 : parseNumber(extraCosts);
    const people = parseInteger(participants);
    const period = parseInteger(periodMonths);
    const influence = parseNumber(influencePct);

    if (!Number.isFinite(cost) || cost <= 0) {
      setValidationError('Укажите стоимость обучения — это число больше нуля.');
      return;
    }
    if (!Number.isFinite(extra) || extra < 0) {
      setValidationError('Дополнительные расходы не могут быть отрицательными.');
      return;
    }
    if (!Number.isInteger(people) || people <= 0) {
      setValidationError('Количество участников должно быть целым числом больше нуля.');
      return;
    }
    if (!Number.isFinite(period) || period < 1 || period > 60) {
      setValidationError('Период оценки должен быть целым числом от 1 до 60 месяцев.');
      return;
    }
    if (!Number.isFinite(influence) || influence < 0 || influence > 100) {
      setValidationError('Доля влияния обучения должна быть в диапазоне от 0 до 100%.');
      return;
    }

    if (type === 'sales') {
      const revenue = parseNumber(monthlyRevenue);
      const growth = parseNumber(revenueGrowthPct);
      const margin = parseNumber(marginPct);
      if (!Number.isFinite(revenue) || revenue <= 0) {
        setValidationError('Укажите текущую месячную выручку команды — это число больше нуля.');
        return;
      }
      if (!Number.isFinite(growth) || growth < 0 || growth > 100) {
        setValidationError('Ожидаемый рост выручки должен быть в диапазоне от 0 до 100%.');
        return;
      }
      if (!Number.isFinite(margin) || margin < 0 || margin > 100) {
        setValidationError('Средняя маржинальность должна быть в диапазоне от 0 до 100%.');
        return;
      }
      setResult(
        calculateRoi('sales', {
          trainingCost: cost,
          extraCosts: extra,
          participants: people,
          monthlyRevenue: revenue,
          revenueGrowthPct: growth,
          marginPct: margin,
          payroll: 0,
          productivityGrowthPct: 0,
          influencePct: influence,
          periodMonths: period,
        }),
      );
    } else {
      const payrollValue = parseNumber(payroll);
      const growth = parseNumber(productivityGrowthPct);
      if (!Number.isFinite(payrollValue) || payrollValue <= 0) {
        setValidationError('Укажите фонд оплаты труда участников в месяц — это число больше нуля.');
        return;
      }
      if (!Number.isFinite(growth) || growth < 0 || growth > 100) {
        setValidationError('Ожидаемый рост производительности должен быть в диапазоне от 0 до 100%.');
        return;
      }
      setResult(
        calculateRoi('productivity', {
          trainingCost: cost,
          extraCosts: extra,
          participants: people,
          monthlyRevenue: 0,
          revenueGrowthPct: 0,
          marginPct: 0,
          payroll: payrollValue,
          productivityGrowthPct: growth,
          influencePct: influence,
          periodMonths: period,
        }),
      );
    }

    setReportStatus('idle');
    setReportUrl('');
    setReportError('');
  };

  const handleReportSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setReportError('');

    if (!reportName.trim()) {
      setReportError('Пожалуйста, укажите ваше имя.');
      return;
    }
    if (!EMAIL_REGEX.test(reportEmail.trim())) {
      setReportError('Пожалуйста, укажите корректный email.');
      return;
    }
    if (!consent) {
      setReportError('Пожалуйста, подтвердите согласие на обработку персональных данных.');
      return;
    }

    setReportStatus('submitting');

    const payload = {
      name: reportName.trim(),
      email: reportEmail.trim(),
      currency,
      calculation_type: type,
      training_cost: parseNumber(trainingCost),
      extra_costs: extraCosts.trim() === '' ? 0 : parseNumber(extraCosts),
      participants: parseInteger(participants),
      monthly_revenue: type === 'sales' ? parseNumber(monthlyRevenue) : null,
      revenue_growth_pct: type === 'sales' ? parseNumber(revenueGrowthPct) : null,
      margin_pct: type === 'sales' ? parseNumber(marginPct) : null,
      payroll: type === 'productivity' ? parseNumber(payroll) : null,
      productivity_growth_pct: type === 'productivity' ? parseNumber(productivityGrowthPct) : null,
      influence_pct: parseNumber(influencePct),
      period_months: parseInteger(periodMonths),
      utm: captureUtm(),
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      consent: true,
      website: honeypot,
    };

    try {
      const response = await fetch(REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let data: { success?: boolean; report_url?: string } = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok && data.success && data.report_url) {
        setReportStatus('success');
        setReportUrl(data.report_url);
      } else {
        setReportStatus('error');
        setReportError('Не удалось сформировать отчёт. Попробуйте ещё раз через несколько минут.');
      }
    } catch {
      setReportStatus('error');
      setReportError('Не удалось сформировать отчёт. Попробуйте ещё раз через несколько минут.');
    }
  };

  const breakevenLabel =
    type === 'sales' ? 'Минимальный необходимый рост выручки' : 'Минимальный необходимый рост производительности';

  const scrollToReport = () => {
    document.getElementById('roi-report-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="roi-calculator" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            Рассчитайте потенциальную окупаемость корпоративного обучения
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Введите несколько параметров — и получите предварительный расчёт финансового эффекта, ROI и точки безубыточности вашего проекта обучения.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 md:p-10">
          <div className="space-y-8">
            <div>
              <span className="block text-sm font-semibold text-gray-900 mb-3">Валюта расчёта</span>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(CURRENCY_LABELS) as Currency[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      currency === c
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2 leading-snug">
                Все суммы указываются в одной выбранной валюте без конвертации.
              </p>
            </div>

            <div>
              <span className="block text-sm font-semibold text-gray-900 mb-3">Цель обучения</span>
              <div className="inline-flex p-1 bg-gray-100 rounded-full">
                <button
                  type="button"
                  onClick={() => changeType('sales')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    type === 'sales' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Увеличение продаж / выручки
                </button>
                <button
                  type="button"
                  onClick={() => changeType('productivity')}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    type === 'productivity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Повышение производительности
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
              <NumberField
                id="roi-cost"
                label="Стоимость обучения"
                value={trainingCost}
                onChange={setTrainingCost}
                required
                suffix={currency}
                placeholder="Например, 5000"
              />
              <NumberField
                id="roi-extra"
                label="Дополнительные расходы"
                hint="Командировки, аренда, материалы и другие сопутствующие затраты."
                value={extraCosts}
                onChange={setExtraCosts}
                min={0}
                suffix={currency}
                placeholder="0"
              />
              <NumberField
                id="roi-participants"
                label="Количество участников"
                value={participants}
                onChange={setParticipants}
                required
                min={1}
                step="1"
                placeholder="Например, 12"
              />

              {type === 'sales' ? (
                <>
                  <NumberField
                    id="roi-revenue"
                    label="Текущая месячная выручка команды"
                    value={monthlyRevenue}
                    onChange={setMonthlyRevenue}
                    required
                    min={0}
                    suffix={currency}
                    placeholder="Например, 200000"
                  />
                  <NumberField
                    id="roi-growth"
                    label="Ожидаемый рост выручки после обучения, %"
                    value={revenueGrowthPct}
                    onChange={setRevenueGrowthPct}
                    required
                    min={0}
                    max={100}
                    suffix="%"
                    placeholder="Например, 10"
                  />
                  <NumberField
                    id="roi-margin"
                    label="Средняя маржинальность, %"
                    hint="Доля прибыли в выручке после переменных затрат."
                    value={marginPct}
                    onChange={setMarginPct}
                    required
                    min={0}
                    max={100}
                    suffix="%"
                    placeholder="Например, 30"
                  />
                </>
              ) : (
                <>
                  <NumberField
                    id="roi-payroll"
                    label="Фонд оплаты труда участников в месяц"
                    hint="Суммарная зарплата всех участников обучения за месяц."
                    value={payroll}
                    onChange={setPayroll}
                    required
                    min={0}
                    suffix={currency}
                    placeholder="Например, 40000"
                  />
                  <NumberField
                    id="roi-prod-growth"
                    label="Ожидаемый рост производительности, %"
                    value={productivityGrowthPct}
                    onChange={setProductivityGrowthPct}
                    required
                    min={0}
                    max={100}
                    suffix="%"
                    placeholder="Например, 8"
                  />
                </>
              )}

              <NumberField
                id="roi-influence"
                label="Доля влияния обучения на результат, %"
                hint="Оценка того, какая часть изменения результата может быть обоснованно связана именно с обучением, а не с другими факторами (рынок, сезонность, мотивация)."
                value={influencePct}
                onChange={setInfluencePct}
                required
                min={0}
                max={100}
                suffix="%"
                placeholder="Например, 40"
              />
              <NumberField
                id="roi-period"
                label="Период оценки эффекта, месяцев"
                value={periodMonths}
                onChange={setPeriodMonths}
                required
                min={1}
                max={60}
                step="1"
                placeholder="Например, 12"
              />
            </div>

            {validationError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {validationError}
              </div>
            )}

            <button
              type="button"
              onClick={handleCalculate}
              className="w-full sm:w-auto bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              Рассчитать ROI
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {result && (
          <div ref={resultRef} className="mt-8 md:mt-10 space-y-8 scroll-mt-24">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 md:p-10">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Ваш предварительный расчёт</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-5">
                  <span className="block text-sm text-gray-500 mb-1">Общие инвестиции</span>
                  <span className="block text-2xl font-bold text-gray-900">{formatMoney(result.totalInvestment, currency)}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <span className="block text-sm text-gray-500 mb-1">Потенциальный финансовый эффект</span>
                  <span className="block text-2xl font-bold text-teal-700">{formatMoney(result.financialEffect, currency)}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <span className="block text-sm text-gray-500 mb-1">Чистый эффект</span>
                  <span className={`block text-2xl font-bold ${result.netEffect >= 0 ? 'text-teal-700' : 'text-red-600'}`}>
                    {formatSignedMoney(result.netEffect, currency)}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm text-gray-500">ROI</span>
                    <span className="relative group inline-flex">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-500 text-[11px] font-semibold cursor-help select-none">?</span>
                      <span className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none leading-snug">
                        Показывает чистый финансовый эффект относительно вложений в обучение.
                      </span>
                    </span>
                  </div>
                  <span className={`block text-2xl font-bold ${result.roiPct >= 0 ? 'text-teal-700' : 'text-red-600'}`}>
                    {formatPercent(result.roiPct)}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <span className="block text-sm text-gray-500 mb-1">Точка безубыточности</span>
                  <button
                    type="button"
                    onClick={scrollToReport}
                    className="relative block w-full text-left cursor-pointer group"
                    aria-label="Доступно в подробном отчёте"
                  >
                    <span className="block text-2xl font-bold text-gray-900 blur-[6px] select-none pointer-events-none" aria-hidden="true">
                      {formatPercent(result.breakevenPct)}
                    </span>
                    <span className="absolute inset-0 flex items-center justify-center gap-1.5">
                      <i className="ri-lock-2-line text-gray-600 text-sm"></i>
                      <span className="text-[11px] font-semibold text-teal-700 leading-tight">Доступно в подробном отчёте</span>
                    </span>
                  </button>
                  <span className="block text-xs text-gray-500 mt-1 leading-snug">{breakevenLabel}</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <span className="block text-sm text-gray-500 mb-1">На 1 {currency} вложений</span>
                  <span className="block text-2xl font-bold text-gray-900">
                    {formatNumber(result.returnPerUnit)} {currency} эффекта
                  </span>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-6 leading-relaxed">
                Расчёт основан на введённых вами параметрах. Это модель потенциальной окупаемости, а не гарантия финансового результата обучения.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 md:p-10">
              <h4 className="text-base md:text-lg font-bold text-gray-900 mb-4">Три сценария эффекта</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {result.scenarios.map((scenario) => (
                  <div key={scenario.label} className="bg-gray-50 rounded-xl p-5">
                    <span className="block text-sm font-semibold text-gray-700 mb-2">{scenario.label}</span>
                    <button
                      type="button"
                      onClick={scrollToReport}
                      className="relative block w-full text-left cursor-pointer group"
                      aria-label="Доступно в подробном отчёте"
                    >
                      <span className="block blur-[6px] select-none pointer-events-none" aria-hidden="true">
                        <span className="block text-lg font-bold text-gray-900">{formatMoney(scenario.effect, currency)}</span>
                        <span className="block text-sm text-gray-600 mt-1">Чистый эффект: {formatSignedMoney(scenario.netEffect, currency)}</span>
                        <span className="block text-sm text-gray-500 mt-1">ROI {formatPercent(scenario.roiPct)}</span>
                      </span>
                      <span className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                        <i className="ri-lock-2-line text-gray-500 text-base"></i>
                        <span className="text-[11px] font-semibold text-teal-700 leading-tight text-center px-2">Доступно в подробном отчёте</span>
                      </span>
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4 leading-snug">
                Сценарии рассчитаны относительно ожидаемого финансового эффекта: консервативный — 50%, базовый — 100%, оптимистичный — 150%. Инвестиции одинаковы во всех сценариях.
              </p>
            </div>

            <div id="roi-report-form" className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-8 md:p-10 scroll-mt-24">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Получите PDF-версию для печати + практическое пособие</h3>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                Пришлём полный расчёт с детальной разбивкой, тремя сценариями и точкой безубыточности — удобно для печати или пересылки руководству. Плюс: практическое пособие по развитию команды в подарок
              </p>

              {reportStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold mb-1">Готово. Персональный отчёт сформирован.</p>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                        Мы отправили ссылку на указанный email. Вы также можете открыть отчёт прямо сейчас.
                      </p>
                      {reportUrl && (
                        <a
                          href={reportUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Открыть мой отчёт
                          <i className="ri-external-link-line"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleReportSubmit} noValidate className="space-y-5">
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="opacity-0 absolute -z-10 pointer-events-none"
                    style={{ left: '-9999px' }}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="roi-report-name" className="block text-sm font-semibold text-gray-900 mb-1.5">
                        Имя <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="roi-report-name"
                        type="text"
                        name="name"
                        value={reportName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setReportName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                        placeholder="Ваше имя"
                      />
                    </div>
                    <div>
                      <label htmlFor="roi-report-email" className="block text-sm font-semibold text-gray-900 mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="roi-report-email"
                        type="email"
                        name="email"
                        value={reportEmail}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setReportEmail(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                        placeholder="you@company.com"
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">
                      Я согласен(на) на обработку персональных данных и отправку запрошенного отчёта.
                    </span>
                  </label>

                  {reportError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      {reportError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={reportStatus === 'submitting'}
                    className="w-full sm:w-auto bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
                  >
                    {reportStatus === 'submitting' ? 'Формируем отчёт...' : 'Получить мой отчёт'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}